import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { motion, AnimatePresence } from "framer-motion";
import "./css/confirmacionSummary.css";

const ConfirmacionSummary = ({ summary }) => {
  const [activeTabPerTalent, setActiveTabPerTalent] = useState({});

  if (!summary || !summary.confirmaciones) {
    return null;
  }

  const handleTabChange = (talentoId, tabIndex) => {
    setActiveTabPerTalent((prev) => ({
      ...prev,
      [talentoId]: tabIndex,
    }));
  };

  const getActiveTab = (talentoId) => activeTabPerTalent[talentoId] || 0;

  return (
    <div className="row">
      <div className="col-12">
        {/* ── Header Card ── */}
        <motion.div
          className="card radius-12 mb-20 cs-header-card"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <div className="card-body p-20 text-center">
            {/* Icono con animación de entrada */}
            <motion.div
              className="cs-success-icon-wrap"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                delay: 0.2,
                type: "spring",
                stiffness: 200,
                damping: 14,
              }}
            >
              <motion.div
                className="cs-success-ring"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1.4, opacity: 0 }}
                transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" }}
              />
              <div className="cs-success-icon">
                <Icon icon="mdi:check-circle" />
              </div>
            </motion.div>

            <motion.h2
              className="text-xl fw-bold text-primary-600 mb-8 cs-header-title"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.4 }}
            >
              ¡Confirmación Completada!
            </motion.h2>

            <motion.p
              className="text-md mb-0 cs-header-subtitle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.4 }}
            >
              Has confirmado <strong>{summary.count}</strong>{" "}
              {summary.count === 1 ? "talento" : "talentos"}
            </motion.p>

            {/* Dots decorativos de talentos */}
            <motion.div
              className="cs-talent-dots"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.4 }}
            >
              {Array.from({ length: summary.count }, (_, i) => (
                <motion.div
                  key={i}
                  className="cs-talent-dot"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    delay: 0.65 + i * 0.07,
                    type: "spring",
                    stiffness: 260,
                    damping: 16,
                  }}
                />
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* ── Talentos grid ── */}
      {summary.confirmaciones.map((confirmacion, cardIndex) => {
        const activeTab = getActiveTab(confirmacion.confirmacion_id);

        return (
          <motion.div
            key={confirmacion.confirmacion_id}
            className="col-lg-6 col-12 mb-20"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.15 + cardIndex * 0.1,
              duration: 0.45,
              ease: "easeOut",
            }}
          >
            <div className="card radius-12 h-100 cs-talent-card">
              <div className="card-body p-20">
                {/* Header del talento */}
                <div className="cs-talent-header">
                  <div className="d-flex align-items-center gap-2">
                    <div className="cs-talent-star-wrap">
                      <Icon icon="mdi:star" />
                    </div>
                    <h3 className="cs-talent-name mb-0">
                      {confirmacion.talento}
                    </h3>
                  </div>
                  <span className="cs-talent-badge">
                    {confirmacion.total_selecciones}{" "}
                    {confirmacion.total_selecciones === 1
                      ? "selección"
                      : "selecciones"}
                  </span>
                </div>

                {/* Tabs */}
                <div className="cs-tabs-wrap">
                  {confirmacion.resumen.map((_, qIndex) => (
                    <button
                      key={qIndex}
                      className={`cs-tab-btn ${activeTab === qIndex ? "active" : ""}`}
                      onClick={() =>
                        handleTabChange(confirmacion.confirmacion_id, qIndex)
                      }
                      type="button"
                    >
                      P{qIndex + 1}
                      {activeTab === qIndex && (
                        <motion.div
                          className="cs-tab-indicator"
                          layoutId={`tab-indicator-${confirmacion.confirmacion_id}`}
                        />
                      )}
                    </button>
                  ))}
                </div>

                {/* Tab content */}
                <AnimatePresence mode="wait">
                  {confirmacion.resumen.map((pregunta, qIndex) =>
                    activeTab === qIndex ? (
                      <motion.div
                        key={qIndex}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                      >
                        {/* Pregunta */}
                        <div className="cs-question-box">
                          <div className="cs-question-icon">
                            <Icon icon="mdi:help-circle-outline" />
                          </div>
                          <p className="cs-question-text mb-0">
                            {pregunta.pregunta}
                          </p>
                        </div>

                        {/* Frases seleccionadas */}
                        <div className="cs-frases-list">
                          {pregunta.frases_seleccionadas.length === 0 ? (
                            <div className="cs-empty-state">
                              <Icon icon="mdi:minus-circle-outline" />
                              <span>Sin selecciones en esta pregunta</span>
                            </div>
                          ) : (
                            pregunta.frases_seleccionadas.map(
                              (frase, fIndex) => (
                                <motion.div
                                  key={fIndex}
                                  className="cs-frase-item"
                                  initial={{ opacity: 0, x: -12 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{
                                    delay: fIndex * 0.07,
                                    duration: 0.3,
                                  }}
                                >
                                  <div className="cs-frase-check">
                                    <Icon icon="mdi:check" />
                                  </div>
                                  <span className="cs-frase-text">{frase}</span>
                                </motion.div>
                              ),
                            )
                          )}
                        </div>
                      </motion.div>
                    ) : null,
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        );
      })}

      {/* ── Botón de retorno ── */}
      <div className="col-12">
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.5 + summary.confirmaciones.length * 0.1,
            duration: 0.4,
          }}
        >
          <a href="/#/mis-talentos" className="cs-back-btn">
            <Icon icon="mdi:arrow-left" />
            <span>Volver a Mis Talentos</span>
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default ConfirmacionSummary;
